'use client';

import { ReactNode } from 'react';

interface CaseStudyQuoteProps {
  /**
   * The quote itself. Pass a string for a single paragraph, or an array for a
   * multi-paragraph quote — the curly quotation marks are added around the
   * whole block, not around each paragraph.
   */
  quote: ReactNode | ReactNode[];
  /** Name, role and company — e.g. "Davey, Customer Support & IT Specialist at CookingLife" */
  attribution: ReactNode;
}

/**
 * Customer quote block for a case study body.
 *
 * Until the 2026-09 testimonial round only the OTO story quoted a human, and
 * it hand-rolled this markup inline. Five more written testimonials arrived at
 * once, so the markup lives here instead of being pasted six times.
 *
 * Placement convention: after the "what Open does" section, before The Results
 * — the quote should land once the reader knows what the agent actually does,
 * and before the numbers that back it up.
 */
export const CaseStudyQuote = ({ quote, attribution }: CaseStudyQuoteProps) => {
  const paragraphs = Array.isArray(quote) ? quote : [quote];

  return (
    <blockquote className="not-prose my-10 rounded-2xl border border-neutral-200 bg-[#fafaf9] p-7">
      {paragraphs.map((paragraph, i) => (
        <p key={i} className="text-lg leading-snug text-neutral-800 [&+p]:mt-4">
          {i === 0 ? '“' : null}
          {paragraph}
          {i === paragraphs.length - 1 ? '”' : null}
        </p>
      ))}
      <p className="mt-4 text-sm font-medium text-neutral-600">{attribution}</p>
    </blockquote>
  );
};
